import {useEffect, useState} from "react";
import {Button} from "../../../basic/button";
import {Checkbox} from "../../../basic/checkbox";
import {Label} from "../../../basic/label";
import {Input} from "../../../basic/input";
import {Badge} from "../../../basic/badge";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";
import {Separator} from "../../../basic/separator";
import {Settings, Tag} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useUpdateTeamByIdMutation,
    VwpTeam,
    VwpTeamCategory
} from "../../../../store/generated/api";

interface TeamSettingsSectionProps {
    selectedTeam: VwpTeam;
    onTeamUpdate?: () => void;
}

const TEAM_CATEGORIES: VwpTeamCategory[] = ["UNIVERSITY", "ALUMNI", "STUDENT", "PRIVATE", "ENTERPRISE"];

export function TeamSettingsSection({selectedTeam, onTeamUpdate}: TeamSettingsSectionProps) {
    const {t} = useTranslationWithKeys();
    const {handleApiError} = useErrorHandler();

    const educationalSettings = selectedTeam.educationalSettings;
    const isUniversity = selectedTeam.isUniversity === true;
    const isLocked = educationalSettings?.settingsLocked === true;

    const [categories, setCategories] = useState<VwpTeamCategory[]>(selectedTeam.categories || []);
    const [adminCap, setAdminCap] = useState<string>(
        selectedTeam.maxAdminCount != null ? String(selectedTeam.maxAdminCount) : ""
    );
    const [semester1Start, setSemester1Start] = useState(educationalSettings?.semesterStartDates?.[0] ?? "");
    const [semester1End, setSemester1End] = useState(educationalSettings?.semesterEndDates?.[0] ?? "");
    const [semester2Start, setSemester2Start] = useState(educationalSettings?.semesterStartDates?.[1] ?? "");
    const [semester2End, setSemester2End] = useState(educationalSettings?.semesterEndDates?.[1] ?? "");
    const [updateTeamById, {isLoading}] = useUpdateTeamByIdMutation();

    useEffect(() => {
        setCategories(selectedTeam.categories || []);
        setAdminCap(selectedTeam.maxAdminCount != null ? String(selectedTeam.maxAdminCount) : "");
        setSemester1Start(educationalSettings?.semesterStartDates?.[0] ?? "");
        setSemester1End(educationalSettings?.semesterEndDates?.[0] ?? "");
        setSemester2Start(educationalSettings?.semesterStartDates?.[1] ?? "");
        setSemester2End(educationalSettings?.semesterEndDates?.[1] ?? "");
    }, [selectedTeam.id, selectedTeam.categories, selectedTeam.maxAdminCount, educationalSettings]);

    const normalizeCategories = (values: VwpTeamCategory[]) => [...values].sort().join(",");

    const parsedAdminCap = adminCap.trim() === "" ? null : Number(adminCap);
    const adminCapChanged = parsedAdminCap !== (selectedTeam.maxAdminCount ?? null);

    const educationalChanged =
        semester1Start !== (educationalSettings?.semesterStartDates?.[0] ?? "") ||
        semester1End !== (educationalSettings?.semesterEndDates?.[0] ?? "") ||
        semester2Start !== (educationalSettings?.semesterStartDates?.[1] ?? "") ||
        semester2End !== (educationalSettings?.semesterEndDates?.[1] ?? "");

    const hasChanges =
        normalizeCategories(categories) !== normalizeCategories(selectedTeam.categories || []) ||
        adminCapChanged ||
        educationalChanged;

    const toggleCategory = (category: VwpTeamCategory, checked: boolean) => {
        if (checked) {
            setCategories(prev => prev.includes(category) ? prev : [...prev, category]);
            return;
        }
        setCategories(prev => prev.filter(c => c !== category));
    };

    const handleSave = async () => {
        if (!hasChanges || parsedAdminCap != null && (!Number.isInteger(parsedAdminCap) || parsedAdminCap < 0)) {
            return;
        }

        try {
            await updateTeamById({
                teamId: selectedTeam.id,
                vwpTeamUpdateRequest: {
                    categories,
                    maxAdminCount: parsedAdminCap,
                    ...(educationalChanged && {
                        educationalSettings: {
                            semesterStartDates: [semester1Start, semester2Start],
                            semesterEndDates: [semester1End, semester2End],
                        }
                    })
                }
            }).unwrap();

            toast.success(t('admin.teamDetails.teamSettings.updated'));
            onTeamUpdate?.();
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.teamManagement.errors.updateFailed')});
        }
    };

    return (
        <CollapsibleSection
            title={t('admin.teamDetails.teamSettings.title')}
            icon={<Settings className="h-5 w-5"/>}
            defaultOpen={true}
            className="mb-4"
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground"/>
                        <h4 className="font-medium">{t('admin.teamDetails.teamSettings.categories')}</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {TEAM_CATEGORIES.map((category) => {
                            const checked = categories.includes(category);
                            return (
                                <label key={category} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={(value) => toggleCategory(category, !!value)}
                                    />
                                    <Label>{category}</Label>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <Separator/>

                <div className="space-y-2">
                    <h4 className="font-medium">{t('admin.teamDetails.teamSettings.adminCap')}</h4>
                    <p className="text-muted-foreground text-sm">
                        {t('admin.teamDetails.teamSettings.adminCapDescription')}
                    </p>
                    <Input
                        type="number"
                        min={0}
                        value={adminCap}
                        onChange={(e) => setAdminCap(e.target.value)}
                        placeholder={t('admin.teamDetails.teamSettings.unlimited')}
                    />
                </div>

                {isUniversity && (
                    <>
                        <Separator/>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <h4 className="font-medium">{t('admin.teamDetails.educationalSettings.title')}</h4>
                                {isLocked && (
                                    <Badge variant="warning">{t('admin.teamDetails.educationalSettings.locked')}</Badge>
                                )}
                            </div>
                            <p className="text-muted-foreground text-sm">
                                {t('admin.teamDetails.educationalSettings.description')}
                            </p>

                            <div className="space-y-4">
                                <h5 className="text-sm font-medium">{t('admin.teamDetails.educationalSettings.semester1')}</h5>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="semester1Start">{t('admin.teamDetails.educationalSettings.startDate')}</Label>
                                        <Input
                                            id="semester1Start"
                                            type="date"
                                            value={semester1Start}
                                            onChange={(e) => setSemester1Start(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="semester1End">{t('admin.teamDetails.educationalSettings.endDate')}</Label>
                                        <Input
                                            id="semester1End"
                                            type="date"
                                            value={semester1End}
                                            onChange={(e) => setSemester1End(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h5 className="text-sm font-medium">{t('admin.teamDetails.educationalSettings.semester2')}</h5>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="semester2Start">{t('admin.teamDetails.educationalSettings.startDate')}</Label>
                                        <Input
                                            id="semester2Start"
                                            type="date"
                                            value={semester2Start}
                                            onChange={(e) => setSemester2Start(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="semester2End">{t('admin.teamDetails.educationalSettings.endDate')}</Label>
                                        <Input
                                            id="semester2End"
                                            type="date"
                                            value={semester2End}
                                            onChange={(e) => setSemester2End(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <Button onClick={handleSave} disabled={!hasChanges || isLoading} className="w-full">
                    {isLoading ? t('common.saving') : t('common.saveChanges')}
                </Button>
            </div>
        </CollapsibleSection>
    );
}
